import React, { useState, useEffect, useRef } from "react";
import { Plus, Check } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "src/components/ui/sheet";
import { Button } from "src/components/ui/button";
import BorderedSelect from "src/components/form-new/bordered-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import BorderedInput from "src/components/form-new/bordered-input";
import { formatCurrency } from "../../utils/billing-utils";
import {
  useCreateCommissionPnlMutation,
  useUpdateCommissionPnlMutation,
} from "../endpoints/commissions.endpoints";
import type { GrossMarginRow } from "../types/commissions.types";

/** Row being edited; _isNew marks a draft row not yet saved */
type EditingRow = GrossMarginRow & { _isNew?: boolean };

function monthName(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleString("default", {
    month: "long",
  });
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  return {
    value: String(month),
    label: new Date(2000, i, 1).toLocaleString("default", { month: "long" }),
  };
});

function getYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => {
    const y = currentYear - i;
    return { value: String(y), label: String(y) };
  });
}

/** Round to 2 decimal places for total income and COGS */
function roundTo2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** True if (month, year) is current month or a past month (no future) */
function isAllowedMonthYear(month: number, year: number): boolean {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (year > currentYear) return false;
  if (year === currentYear && month > currentMonth) return false;
  return true;
}

interface GrossMarginDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: GrossMarginRow[];
  onSave: (rows: GrossMarginRow[]) => void;
  /** Total income of paid invoices for a given month (used when creating a new entry) */
  getTotalIncomeForMonth?: (month: number, year: number) => number;
}

const GrossMarginDrawer: React.FC<GrossMarginDrawerProps> = ({
  open,
  onOpenChange,
  rows,
  onSave,
  getTotalIncomeForMonth,
}) => {
  const [editing, setEditing] = useState<EditingRow[]>([]);
  const [createPnl, { isLoading: isCreating }] = useCreateCommissionPnlMutation();
  const [updatePnl, { isLoading: isUpdating }] = useUpdateCommissionPnlMutation();
  const originalByKey = useRef<Record<string, { totalIncome: number; cogs: number | null }>>({});

  const yearOptions = getYearOptions();

  useEffect(() => {
    if (open && rows.length > 0) {
      setEditing(
        rows.map((r) => ({
          ...r,
          totalIncome: roundTo2(r.totalIncome),
          cogs: r.cogs != null ? roundTo2(r.cogs) : null,
        } as EditingRow))
      );
      originalByKey.current = rows.reduce<Record<string, { totalIncome: number; cogs: number | null }>>(
        (acc, r) => {
          const key = `${r.year}-${r.month}`;
          acc[key] = {
            totalIncome: roundTo2(r.totalIncome),
            cogs: r.cogs != null ? roundTo2(r.cogs) : null,
          };
          return acc;
        },
        {}
      );
    } else if (open) {
      setEditing([]);
      originalByKey.current = {};
    }
  }, [open, rows]);

  const rowKey = (row: EditingRow) => `${row.year}-${row.month}`;

  const isRowDirty = (index: number) => {
    const row = editing[index];
    if (!row || row._isNew) return false;
    const key = rowKey(row);
    const orig = originalByKey.current[key];
    if (!orig) return false;
    return (
      roundTo2(row.totalIncome) !== orig.totalIncome ||
      (row.cogs ?? null) !== orig.cogs
    );
  };

  const handleSaveRow = async (index: number) => {
    const row = editing[index];
    if (!row || row._isNew || !row.id) return;
    const totalIncome = roundTo2(row.totalIncome);
    const cogs = row.cogs ?? null;
    try {
      await updatePnl({ id: row.id, totalIncome, cogs }).unwrap();
      const key = rowKey(row);
      originalByKey.current[key] = { totalIncome, cogs };
    } catch {
      // Error could be shown via toast
    }
  };

  const handleAddRow = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const totalIncome = roundTo2(getTotalIncomeForMonth?.(month, year) ?? 0);
    setEditing((prev) => [
      ...prev,
      { month, year, totalIncome, cogs: 0, _isNew: true } as EditingRow,
    ]);
  };

  const handleDraftMonthYearChange = (draftIndex: number, month: number, year: number) => {
    const totalIncome = roundTo2(getTotalIncomeForMonth?.(month, year) ?? 0);
    setEditing((prev) =>
      prev.map((row, i) =>
        i === draftIndex && row._isNew
          ? { ...row, month, year, totalIncome }
          : row
      )
    );
  };

  const handleSaveDraft = async (draftIndex: number) => {
    const row = editing[draftIndex];
    if (!row || !row._isNew) return;
    const { month, year, totalIncome } = row;
    if (!isAllowedMonthYear(month, year)) return;
    const alreadyExists = rows.some((r) => r.month === month && r.year === year);
    if (alreadyExists) return;
    try {
      await createPnl({ month, year, totalIncome: roundTo2(totalIncome) }).unwrap();
      setEditing((prev) => prev.filter((_, i) => i !== draftIndex));
    } catch {
      // Error could be shown via toast
    }
  };

  const handleIncomeChange = (index: number, value: string) => {
    const num = parseFloat(value);
    setEditing((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, totalIncome: Number.isNaN(num) ? 0 : roundTo2(num) }
          : row
      )
    );
  };

  const handleCogsChange = (index: number, value: string) => {
    if (value.trim() === "") {
      setEditing((prev) =>
        prev.map((row, i) => (i === index ? { ...row, cogs: null } : row))
      );
      return;
    }
    const num = parseFloat(value);
    setEditing((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, cogs: Number.isNaN(num) ? null : roundTo2(num) } : row
      )
    );
  };

  const hasDraft = editing.some((r) => r._isNew);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!w-[70vw] sm:!w-[85vw] !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle>Gross margin by month</SheetTitle>
        </SheetHeader>
        <div className="py-4 space-y-4">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="med"
              onClick={handleAddRow}
              disabled={hasDraft}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add row
            </Button>
            {hasDraft && (
              <span className="text-xs text-gray-500">
                Save the new row with the checkmark before adding another.
              </span>
            )}
          </div>
          <div className="rounded-md border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 border-b border-gray-200">
                  <TableHead className="font-medium text-gray-700 py-3 px-3">
                    Month
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3">
                    Year
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3">
                    Total Income
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3">
                    Total COGS
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3 text-right">
                    GM
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3 text-right">
                    GM%
                  </TableHead>
                  <TableHead className="font-medium text-gray-700 py-3 px-3 w-[4rem]">
                    {" "}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editing.map((row, index) => {
                  const cogsVal = row.cogs ?? 0;
                  const gm = row.totalIncome - cogsVal;
                  const gmPct =
                    row.totalIncome > 0
                      ? (gm / row.totalIncome) * 100
                      : 0;
                  const isNew = row._isNew === true;
                  const canSaveDraft =
                    isNew &&
                    isAllowedMonthYear(row.month, row.year) &&
                    !rows.some(
                      (r) => r.month === row.month && r.year === row.year
                    );
                  const activeTotalIncome = row.activeTotalIncome;
                  const totalIncomeMismatch =
                    !isNew &&
                    activeTotalIncome != null &&
                    roundTo2(activeTotalIncome) !== roundTo2(row.totalIncome);
                  const isDirty = isRowDirty(index);
                  const canSaveRow = !isNew && isDirty && Boolean(row.id);

                  return (
                    <TableRow
                      key={
                        isNew
                          ? `draft-${index}`
                          : `${row.year}-${row.month}`
                      }
                      className={`border-b border-gray-100 last:border-0 ${isNew ? "bg-gray-50/50" : ""} ${totalIncomeMismatch ? "bg-amber-50" : ""}`}
                    >
                      <TableCell className="py-2 px-3">
                        {isNew ? (
                          <BorderedSelect
                            options={MONTH_OPTIONS}
                            value={String(row.month)}
                            onChange={(e) =>
                              handleDraftMonthYearChange(
                                index,
                                Number(e.target.value),
                                row.year
                              )
                            }
                            className="max-w-[8rem]"
                          />
                        ) : (
                          <span className="text-gray-900">
                            {monthName(row.month)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        {isNew ? (
                          <BorderedSelect
                            options={yearOptions}
                            value={String(row.year)}
                            onChange={(e) =>
                              handleDraftMonthYearChange(
                                index,
                                row.month,
                                Number(e.target.value)
                              )
                            }
                            className="max-w-[6rem]"
                          />
                        ) : (
                          <span className="text-gray-900">{row.year}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        {isNew ? (
                          <span className="tabular-nums text-gray-900">
                            {formatCurrency(row.totalIncome)}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <BorderedInput
                              type="number"
                              min={0}
                              step={0.01}
                              value={String(row.totalIncome)}
                              onChange={(e) =>
                                handleIncomeChange(index, e.target.value)
                              }
                              className="max-w-[10rem]"
                            />
                            {totalIncomeMismatch && activeTotalIncome != null && (
                              <span className="text-xs text-amber-700 tabular-nums">
                                Correct*: {formatCurrency(activeTotalIncome)}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        {isNew ? (
                          <span className="text-gray-500">0</span>
                        ) : (
                          <BorderedInput
                            type="number"
                            min={0}
                            step={0.01}
                            value={row.cogs == null ? "" : String(row.cogs)}
                            onChange={(e) =>
                              handleCogsChange(index, e.target.value)
                            }
                            className="max-w-[10rem]"
                            placeholder="—"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-gray-900 py-2 px-3">
                        {formatCurrency(gm)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-gray-900 py-2 px-3">
                        {gmPct.toFixed(1)}%
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        {isNew ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="smallIcon"
                            className="shrink-0"
                            disabled={!canSaveDraft || isCreating}
                            onClick={() => handleSaveDraft(index)}
                            title="Save new entry"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        ) : canSaveRow ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="smallIcon"
                            className="shrink-0"
                            disabled={isUpdating}
                            onClick={() => handleSaveRow(index)}
                            title="Save changes"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-200">
            <span className="font-medium">*</span> If a corrected value is given,
            this is a calculation of all paid invoices for the given month. If
            you have a reason to change the amount please ignore, otherwise you
            may want to change to the new calculated value.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default GrossMarginDrawer;
